<template>
  <Teleport to="body">
    <div class="videoplayer" :class="{ '-open': isOpen }">
      <dialog ref="videoPlayerEl" class="container">
        <div class="inner">
          <ClientOnly>
            <VideoJSPlayer class="video-player" v-if="currentVideo?.streamingUrl" :options="{
              autoplay: false,
              muted: false,
              controls: false,
              loop: false,
              auto: false,
              fill: true,
              responsive: true,
              playsinline: true,
              hls: {
                limitRenditionByPlayerDimensions: true,
                useDevicePixelRatio: true,
              },
              bandwidth: 10e6,
              sources: [
                {
                  src: currentVideo.streamingUrl,
                  type: 'application/x-mpegURL',
                },
              ],
            }" @player-ready="onReady" @play="onPlay" @pause="onPause" @ended="onEnded" @timeupdate="onTimeUpdate" />
          </ClientOnly>
        </div>
      </dialog>
      <ReturnButton class="return-button" img-class="return-button-img" :is-visible="isOpen" :on-click="onEnded" />
      <div class="controls" :class="{ '-playing': isPlaying }" @click="onPlayPause">
        <img class="icon pause-icon" src="/icons/Pause.svg" /><img class="icon play-icon" src="/icons/Play.svg" />
      </div>

      <div class="progress">
        <div class="bar"></div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import VideoJSPlayer from '~/components/partials/VideoJSPlayer.vue';
import ReturnButton from '~/components/partials/ReturnButton.vue';
import { useScrollLock } from '~/utils/useScrollLock';
import { type ExperienceVideo } from '~/clinic-inside/utils/useVideo';
/*
  Info: this video player when clicking on a video poster in 3d-context
 */

const videoPlayerEl = ref<HTMLDialogElement | null>(null);
const isOpen = ref<boolean>(false);
const isPlaying = ref<boolean>(false);
const currentVideo = ref<ExperienceVideo | null>(null);
const currentVideoIndex = ref<number | null>(null);
let videoPlayer: any;

onMounted(function () {
  if (typeof document !== 'undefined') {
    // @ts-ignore
    document.addEventListener(VideoPlayer.eventVideoStart, onVideoTrigger);
  }
});

function onVideoTrigger(event: CustomEvent) {
  currentVideo.value = event.detail.video;
  currentVideoIndex.value = event.detail.videoIndex;
  openModal();
}

function openModal() {
  isOpen.value = true;
  useScrollLock(true);
  playOpenTimeline();
}

function onPlayPause() {
  if (isPlaying.value) {
    videoPlayer?.pause();
  } else {
    videoPlayer?.play();
  }
  isPlaying.value = !isPlaying.value;
}

function playOpenTimeline() {
  if (videoPlayerEl.value) {
    gsap.set(videoPlayerEl.value, {
      opacity: 0,
    });
    videoPlayerEl.value.show();
    gsap.fromTo(
      videoPlayerEl.value,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.34,
        delay: 0.4,
      },
    );
  }
}

function playCloseTimeline() {
  gsap.to(videoPlayerEl.value, {
    opacity: 0,
    duration: 0.2,
    onComplete: function () {
      (videoPlayerEl.value as HTMLDialogElement).close();
      isOpen.value = false;
      currentVideo.value = null;
      useScrollLock(false);
    },
  });
}

function onReady(player: any) {
  videoPlayer = player;
  requestAnimationFrame(() => {
    videoPlayer?.play();
  });
}

function onPlay() {
  useScrollLock(true);
  isPlaying.value = true;
  gsap.set('.progress .bar', {
    width: 0,
  });
  videoPlayerEl.value?.setAttribute(
    'data-video',
    `video-${currentVideoIndex.value}`,
  );
}

function onPause() {
  isPlaying.value = false;
}

function onEnded() {
  isOpen.value = false;
  VideoPlayer.dispatchVideoClose();
  playCloseTimeline();
  useScrollLock(false);
}

function onTimeUpdate(timePercentage: number) {
  gsap.to('.progress .bar', {
    width: `${timePercentage * 100}%`,
  });
}
</script>

<style scoped lang="scss">
.videoplayer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  justify-content: center;
  pointer-events: none;
  transition: background 0.3s ease-in-out;

  &.-open {
    pointer-events: auto;
    background: rgba(0, 0, 0, 0.6);

    .progress {
      opacity: 1;
    }

    .controls {
      opacity: 1;
    }
  }

  .container {
    margin-inline: auto;
    outline: none;
    border-radius: 5px;
    padding: 0;
    overflow: clip;
    border: 0;
    outline: 9px solid #000000;
    position: relative;
    grid-column: 2 / 3;
    grid-row: 2 / 3;

    >.inner {
      position: relative;
      width: 80vw;
      height: 45vw;
      padding: 0;

      .video-player {
        height: 100%;
      }
    }
  }

  .return-button {
    position: absolute;
    right: -2.15875rem;
    top: -2.15875rem;
    width: 4.375rem;
    height: 4.375rem;
    z-index: 100;
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  .controls {
    position: absolute;
    grid-column: 2 / 3;
    grid-row: 3 / 4;
    top: 1.5rem;
    opacity: 0;

    &.-playing {
      .play-icon {
        display: none;
      }

      .pause-icon {
        display: block;
      }
    }

    .play-icon {
      display: block;
    }

    .pause-icon {
      display: none;
    }
  }

  .progress {
    position: absolute;
    top: 2.875rem;
    width: 100%;
    grid-column: 2 / 3;
    grid-row: 3 / 4;
    opacity: 0;
    background: var(--base-color-white-40);
    display: flex;

    .bar {
      width: 0%;
      height: 4px;
      background: var(--color-white);
    }
  }
}
</style>
<style lang="scss">
.return-button {
  img.return-button-img {
    width: 1.3125;
    height: 1.3125;
  }
}
</style>
